import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Download, Upload } from 'lucide-react';
import { PerformanceGroup } from '@/types/performance-analyze';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface BulkActionsProps {
  groups: PerformanceGroup[];
  onImport: () => void;
}

export const BulkActions = ({ groups, onImport }: BulkActionsProps) => {
  const [importing, setImporting] = useState(false);

  const handleExport = () => {
    const exportData = groups.map(({ id, created_at, updated_at, created_by, ...rest }) => rest);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `performance-groups-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Groups exported successfully');
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    try {
      const text = await file.text();
      const importedGroups = JSON.parse(text);

      if (!Array.isArray(importedGroups)) {
        throw new Error('Invalid file format');
      }

      const { data: userData } = await supabase.auth.getUser();

      // Insert groups
      const { error } = await supabase
        .from('performance_analyze_groups')
        .insert(
          importedGroups.map(group => ({
            ...group,
            created_by: userData?.user?.id
          }))
        );

      if (error) throw error;

      // Log audit
      await supabase.from('performance_analyze_audit_log').insert({
        action: 'import',
        new_config: { count: importedGroups.length },
        changed_by: userData?.user?.id
      });

      toast.success(`Imported ${importedGroups.length} groups successfully`);
      onImport();
    } catch (error: any) {
      toast.error('Failed to import groups: ' + error.message);
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  return (
    <div className="flex gap-2">
      <Button variant="outline" onClick={handleExport}>
        <Download className="h-4 w-4 mr-2" />
        Export Groups
      </Button>
      
      <Button variant="outline" disabled={importing} asChild>
        <label>
          <Upload className="h-4 w-4 mr-2" />
          {importing ? 'Importing...' : 'Import Groups'}
          <input
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
            disabled={importing}
          />
        </label>
      </Button>
    </div>
  );
};
